import { createContext, useContext, useState, ReactNode, useCallback } from "react";

type Locale = "es" | "ca" | "en";

interface I18nContextType {
  locale: Locale;
  setLocale: (l: Locale) => void;
  t: (key: string) => string;
}

const translations: Record<Locale, Record<string, string>> = {
  es: {
    "nav.explore": "Explorar",
    "nav.sell": "Vender",
    "nav.blog": "Blog",
    "nav.login": "Entrar",
    "nav.register": "Registro",
    "nav.profile": "Mi perfil",
    "nav.myListings": "Mis anuncios",
    "nav.favorites": "Favoritos",
    "nav.messages": "Mensajes",
    "nav.admin": "Panel Admin",
    "nav.logout": "Cerrar sesión",
    "nav.guide": "Guía de compra",
    "nav.comparator": "Comparador",
    "nav.calculator": "Calculadora",
    "footer.tagline": "El marketplace líder de patinetes eléctricos en España. Compra y vende de forma segura.",
    "footer.explore": "Explorar",
    "footer.search": "Buscar patinetes",
    "footer.publish": "Publicar anuncio",
    "footer.legal": "Legal",
    "footer.privacy": "Política de Privacidad",
    "footer.terms": "Términos y Condiciones",
    "footer.legalNotice": "Aviso Legal",
    "footer.contact": "Contacto",
    "footer.contactUs": "Contáctanos",
    "footer.rights": "Todos los derechos reservados.",
    "footer.tools": "Herramientas",
  },
  ca: {
    "nav.explore": "Explorar",
    "nav.sell": "Vendre",
    "nav.blog": "Blog",
    "nav.login": "Entrar",
    "nav.register": "Registre",
    "nav.profile": "El meu perfil",
    "nav.myListings": "Els meus anuncis",
    "nav.favorites": "Preferits",
    "nav.messages": "Missatges",
    "nav.admin": "Panell Admin",
    "nav.logout": "Tancar sessió",
    "nav.guide": "Guia de compra",
    "nav.comparator": "Comparador",
    "nav.calculator": "Calculadora",
    "footer.tagline": "El marketplace líder de patinets elèctrics a Espanya. Compra i ven de forma segura.",
    "footer.explore": "Explorar",
    "footer.search": "Buscar patinets",
    "footer.publish": "Publicar anunci",
    "footer.legal": "Legal",
    "footer.privacy": "Política de Privacitat",
    "footer.terms": "Termes i Condicions",
    "footer.legalNotice": "Avís Legal",
    "footer.contact": "Contacte",
    "footer.contactUs": "Contacta'ns",
    "footer.rights": "Tots els drets reservats.",
    "footer.tools": "Eines",
  },
  en: {
    "nav.explore": "Explore",
    "nav.sell": "Sell",
    "nav.blog": "Blog",
    "nav.login": "Login",
    "nav.register": "Sign Up",
    "nav.profile": "My Profile",
    "nav.myListings": "My Listings",
    "nav.favorites": "Favorites",
    "nav.messages": "Messages",
    "nav.admin": "Admin Panel",
    "nav.logout": "Sign Out",
    "nav.guide": "Buying Guide",
    "nav.comparator": "Comparator",
    "nav.calculator": "Calculator",
    "footer.tagline": "The leading electric scooter marketplace in Spain. Buy and sell safely.",
    "footer.explore": "Explore",
    "footer.search": "Search scooters",
    "footer.publish": "Post a listing",
    "footer.legal": "Legal",
    "footer.privacy": "Privacy Policy",
    "footer.terms": "Terms & Conditions",
    "footer.legalNotice": "Legal Notice",
    "footer.contact": "Contact",
    "footer.contactUs": "Contact Us",
    "footer.rights": "All rights reserved.",
    "footer.tools": "Tools",
  },
};

const I18nContext = createContext<I18nContextType>({
  locale: "es",
  setLocale: () => {},
  t: (key) => key,
});

export const useI18n = () => useContext(I18nContext);

export const I18nProvider = ({ children }: { children: ReactNode }) => {
  const [locale, setLocaleState] = useState<Locale>(
    () => (localStorage.getItem("locale") as Locale) || "es"
  );

  const setLocale = useCallback((l: Locale) => {
    setLocaleState(l);
    localStorage.setItem("locale", l);
    document.documentElement.lang = l;
  }, []);

  const t = useCallback((key: string) => translations[locale]?.[key] || translations.es[key] || key, [locale]);

  return (
    <I18nContext.Provider value={{ locale, setLocale, t }}>
      {children}
    </I18nContext.Provider>
  );
};
